import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { db } from "@/config/firebase";
import {
    collection,
    addDoc,
    getDocs,
    doc,
    getDoc,
    where,
    query,
    deleteDoc,
    Timestamp,
    updateDoc,
    onSnapshot,
    orderBy,
    startAfter,
    limit as firebaseLimit,
    DocumentData,
    Query,
} from "firebase/firestore";
import { getCountFromServer } from "firebase/firestore";
import { PersonModelInterface } from "@/models/personModel";
import dayjs from "dayjs";

type DateRangeType = "year" | "month" | "week" | "custom";

interface PeopleContextProps {
    people: PersonModelInterface[];
    prevPeople: PersonModelInterface[];
    closedCredits: PersonModelInterface[];
    prevClosedCredits: PersonModelInterface[];
    totalPayments: PersonModelInterface[];
    prevTotalPayments: PersonModelInterface[];
    loadingPeople: boolean;
    errorPeople: string | null;
    totalCount: number;

    getPeople: (options?: {
        dateRange?: DateRangeType;
        customStart?: Date;
        customEnd?: Date;
        pageSize?: number;
        page?: number;
        search?: string;
    }) => Promise<void>;

    getPrevPeople: (options?: {
        dateRange?: DateRangeType;
        customStart?: Date;
        customEnd?: Date;
    }) => Promise<void>;

    getClosedCredits: (options?: {
        dateRange?: DateRangeType;
        customStart?: Date;
        customEnd?: Date;
        isPrev?: boolean;
    }) => Promise<void>;

    addPerson: (person: PersonModelInterface) => Promise<void>;
    getPersonById: (id: string) => Promise<PersonModelInterface | null>;
    deletePersonById: (id: string) => Promise<void>;
    bulkDelete: (ids: string[]) => Promise<void>;
    updatePerson: (person: PersonModelInterface) => Promise<void>;
    updatePersonStatus: (id: string, status: string) => Promise<void>;
    toggleChecked: (id: string, checked: boolean) => Promise<void>;
}

const PeopleContext = createContext<PeopleContextProps>({
    people: [],
    prevPeople: [],
    closedCredits: [],
    prevClosedCredits: [],
    totalPayments: [],
    prevTotalPayments: [],
    loadingPeople: false,
    errorPeople: null,
    totalCount: 0,
    getPeople: async () => {},
    getPrevPeople: async () => {},
    getClosedCredits: async () => {},
    addPerson: async () => {},
    getPersonById: async () => null,
    deletePersonById: async () => {},
    bulkDelete: async () => {},
    updatePerson: async () => {},
    updatePersonStatus: async () => {},
    toggleChecked: async () => {},
});

export const PeopleProvider = ({ children }: { children: React.ReactNode }) => {
    const [people, setPeople] = useState<PersonModelInterface[]>([]);
    const [prevPeople, setPrevPeople] = useState<PersonModelInterface[]>([]);
    const [totalPayments, setTotalPayments] = useState<PersonModelInterface[]>([]);
    const [prevTotalPayments, setPrevTotalPayments] = useState<PersonModelInterface[]>([]);
    const [closedCredits, setClosedCredits] = useState<PersonModelInterface[]>([]);
    const [prevClosedCredits, setPrevClosedCredits] = useState<PersonModelInterface[]>([]);
    const [loadingPeople, setLoadingPeople] = useState(false);
    const [errorPeople, setErrorPeople] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState<number>(0);

    const fetchTotalCount = useCallback(async () => {
        try {
            const coll = collection(db, "people");
            const snapshot = await getCountFromServer(coll);
            setTotalCount(snapshot.data().count);
        } catch (err: unknown) {
            console.error("Ошибка при получении общего количества клиентов", err);
        }
    }, []);

    useEffect(() => {
        const q = query(collection(db, "people"), orderBy("createdAt", "desc"));
        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const fetchedPeople: PersonModelInterface[] = [];
                snapshot.forEach((docSnapshot) => {
                    const data = docSnapshot.data() as any;
                    fetchedPeople.push({
                        id: docSnapshot.id,
                        ...data,
                        createdAt:
                            data?.createdAt instanceof Timestamp
                                ? data.createdAt.toDate()
                                : data?.createdAt,
                    } as PersonModelInterface);
                });
                setPeople(fetchedPeople);
            },
            (error) => {
                setErrorPeople(error.message);
            }
        );

        return () => unsubscribe();
    }, []);

    const updatePersonStatus = useCallback(async (id: string, status: string) => {
        const ref = doc(db, "people", id);
        await updateDoc(ref, { status });
        setPeople((prev) => prev.map((p) => (p.id === id ? { ...p, status } : p)));
    }, []);

    useEffect(() => {
        fetchTotalCount();

        const now = dayjs();

        people.forEach((person) => {
            if (["closed", "closedEarly", "returned"].includes(person.status)) return;

            const paymentsMade = (person.payments || []).filter((p) => p.type !== "discount").length;
            const release = dayjs(person.creditDetail.releaseDay);

            // сколько платежей "должно было быть" на текущую дату
            let dueCount = 0;
            while (!release.add(dueCount + 1, "month").isAfter(now, "day")) {
                dueCount++;
            }

            // первый платёж переводит из "new" в "pending"
            if (paymentsMade > 0 && person.status === "new") {
                updatePersonStatus(person.id, "pending");
                return;
            }

            // пропущенный платёж → overdue
            if (dueCount > paymentsMade && person.status !== "overdue") {
                updatePersonStatus(person.id, "overdue");
                return;
            }

            // догнал просрочку → pending
            if (person.status === "overdue" && paymentsMade >= dueCount) {
                updatePersonStatus(person.id, "pending");
            }
        });
    }, [people, updatePersonStatus, fetchTotalCount]);

    const getDateRange = useCallback(
        (dateRange: DateRangeType, customStart?: Date, customEnd?: Date, isPrevious?: boolean) => {
            let startDate: Timestamp | null = null;
            let endDate: Timestamp = Timestamp.fromDate(dayjs().endOf("day").toDate());
            const now = dayjs();

            if (dateRange === "year") {
                startDate = Timestamp.fromDate(
                    now.subtract(isPrevious ? 1 : 0, "year").startOf("year").toDate()
                );
                endDate = Timestamp.fromDate(
                    now.subtract(isPrevious ? 1 : 0, "year").endOf("year").toDate()
                );
            } else if (dateRange === "month") {
                startDate = Timestamp.fromDate(
                    now.subtract(isPrevious ? 1 : 0, "month").startOf("month").toDate()
                );
                endDate = Timestamp.fromDate(
                    now.subtract(isPrevious ? 1 : 0, "month").endOf("month").toDate()
                );
            } else if (dateRange === "week") {
                startDate = Timestamp.fromDate(
                    now.subtract(isPrevious ? 1 : 0, "week").startOf("isoWeek").toDate()
                );
                endDate = Timestamp.fromDate(
                    now.subtract(isPrevious ? 1 : 0, "week").endOf("isoWeek").toDate()
                );
            } else if (dateRange === "custom" && customStart && customEnd) {
                startDate = Timestamp.fromDate(dayjs(customStart).startOf("day").toDate());
                endDate = Timestamp.fromDate(dayjs(customEnd).endOf("day").toDate());
            }

            return { startDate, endDate };
        },
        []
    );

    const getPrevPeople = useCallback(
        async (options?: { dateRange?: DateRangeType; customStart?: Date; customEnd?: Date }) => {
            try {
                setLoadingPeople(true);
                setErrorPeople(null);

                let baseQuery: Query<DocumentData> = collection(db, "people");

                if (options?.dateRange) {
                    const { startDate, endDate } = getDateRange(
                        options.dateRange,
                        options.customStart,
                        options.customEnd,
                        true
                    );

                    if (startDate) {
                        baseQuery = query(
                            baseQuery,
                            where("createdAt", ">=", startDate),
                            where("createdAt", "<=", endDate),
                            orderBy("createdAt", "desc")
                        );
                    }
                }

                const snap = await getDocs(baseQuery);

                const fetched: PersonModelInterface[] = snap.docs.map((d) => {
                    const data = d.data() as any;
                    return {
                        id: d.id,
                        ...data,
                        createdAt: data?.createdAt instanceof Timestamp ? data.createdAt.toDate() : data?.createdAt,
                    } as PersonModelInterface;
                });

                setPrevPeople(fetched);
            } catch (err) {
                setErrorPeople(err instanceof Error ? err.message : "Ошибка при загрузке данных");
            } finally {
                setLoadingPeople(false);
            }
        },
        [getDateRange]
    );

    const getPeople = useCallback(
        async (options?: {
            dateRange?: DateRangeType;
            customStart?: Date;
            customEnd?: Date;
            pageSize?: number;
            page?: number;
            search?: string;
        }) => {
            try {
                setLoadingPeople(true);
                setErrorPeople(null);

                let baseQuery: Query<DocumentData> = collection(db, "people");

                if (options?.dateRange) {
                    const { startDate, endDate } = getDateRange(options.dateRange, options.customStart, options.customEnd);

                    if (startDate) {
                        baseQuery = query(
                            baseQuery,
                            where("createdAt", ">=", startDate),
                            where("createdAt", "<=", endDate)
                        );
                    }
                }

                baseQuery = query(baseQuery, orderBy("createdAt", "desc"));

                // если search: тянем все под фильтр и режем на клиенте (для firestore так проще)
                if (options?.search) {
                    const snap = await getDocs(baseQuery);

                    let fetched: PersonModelInterface[] = snap.docs.map((d) => {
                        const data = d.data() as any;
                        return {
                            id: d.id,
                            ...data,
                            createdAt: data?.createdAt instanceof Timestamp ? data.createdAt.toDate() : data?.createdAt,
                        } as PersonModelInterface;
                    });

                    const searchLower = options.search.toLowerCase();
                    fetched = fetched.filter(
                        (person) =>
                            person.name?.toLowerCase().includes(searchLower) ||
                            person.phone?.phoneModel?.toLowerCase().includes(searchLower)
                    );

                    const pageSize = options.pageSize || 10;
                    const page = options.page || 1;
                    const startIndex = (page - 1) * pageSize;

                    setPeople(fetched.slice(startIndex, startIndex + pageSize));
                    setTotalCount(fetched.length);
                    return;
                }

                // пагинация без поиска
                let q: Query<DocumentData> = baseQuery;

                if (options?.pageSize && options?.page) {
                    const startIndex = (options.page - 1) * options.pageSize;

                    if (options.page === 1) {
                        q = query(baseQuery, firebaseLimit(options.pageSize));
                    } else {
                        const prevQ = query(baseQuery, firebaseLimit(startIndex));
                        const prevSnap = await getDocs(prevQ);
                        const lastDoc = prevSnap.docs[prevSnap.docs.length - 1];

                        q = lastDoc
                            ? query(baseQuery, startAfter(lastDoc), firebaseLimit(options.pageSize))
                            : query(baseQuery, firebaseLimit(options.pageSize));
                    }
                }

                const snap = await getDocs(q);

                const fetched: PersonModelInterface[] = snap.docs.map((d) => {
                    const data = d.data() as any;
                    return {
                        id: d.id,
                        ...data,
                        createdAt: data?.createdAt instanceof Timestamp ? data.createdAt.toDate() : data?.createdAt,
                    } as PersonModelInterface;
                });

                setPeople(fetched);
                await fetchTotalCount();
            } catch (err: unknown) {
                setErrorPeople(err instanceof Error ? err.message : "Ошибка при загрузке списка людей");
            } finally {
                setLoadingPeople(false);
            }
        },
        [fetchTotalCount, getDateRange]
    );

    const addPerson = useCallback(
        async (person: PersonModelInterface) => {
            try {
                await addDoc(collection(db, "people"), {
                    ...person,
                    createdAt: Timestamp.now(),
                });
                await fetchTotalCount();
            } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : "Ошибка при добавлении человека";
                setErrorPeople(msg);
                throw new Error(msg);
            }
        },
        [fetchTotalCount]
    );

    const getPersonById = useCallback(async (id: string): Promise<PersonModelInterface | null> => {
        try {
            const ref = doc(db, "people", id);
            const snap = await getDoc(ref);
            if (!snap.exists()) return null;

            const data = snap.data() as any;
            return {
                id: snap.id,
                ...data,
                createdAt: data?.createdAt instanceof Timestamp ? data.createdAt.toDate() : data?.createdAt,
            } as PersonModelInterface;
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : "Ошибка при получении данных клиента";
            setErrorPeople(msg);
            return null;
        }
    }, []);

    const deletePersonById = useCallback(
        async (id: string): Promise<void> => {
            try {
                const ref = doc(db, "people", id);
                await deleteDoc(ref);
                setPeople((prev) => prev.filter((p) => p.id !== id));
                await fetchTotalCount();
            } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : "Ошибка при удалении клиента";
                setErrorPeople(msg);
                throw new Error(msg);
            }
        },
        [fetchTotalCount]
    );

    const bulkDelete = useCallback(
        async (ids: string[]): Promise<void> => {
            try {
                await Promise.all(
                    ids.map(async (id) => {
                        const ref = doc(db, "people", id);
                        await deleteDoc(ref);
                    })
                );
                setPeople((prev) => prev.filter((p) => !ids.includes(p.id)));
                await fetchTotalCount();
            } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : "Ошибка при массовом удалении клиентов";
                setErrorPeople(msg);
                throw new Error(msg);
            }
        },
        [fetchTotalCount]
    );

    const updatePerson = useCallback(async (person: PersonModelInterface): Promise<void> => {
        if (!person.id) throw new Error("ID отсутствует у пользователя");

        try {
            const { id, ...updateData } = person;
            const ref = doc(db, "people", id);
            await updateDoc(ref, updateData as any);
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : "Ошибка при обновлении клиента";
            setErrorPeople(msg);
            throw new Error(msg);
        }
    }, []);

    const toggleChecked = useCallback(
        async (id: string, newChecked?: boolean): Promise<void> => {
            const person = people.find((p) => p.id === id);
            if (!person) return;

            const checked = newChecked !== undefined ? newChecked : !person.checked;

            try {
                const ref = doc(db, "people", id);
                await updateDoc(ref, { checked });
                setPeople((prev) => prev.map((p) => (p.id === id ? { ...p, checked } : p)));
            } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : "Ошибка при обновлении флага checked";
                setErrorPeople(msg);
                throw new Error(msg);
            }
        },
        [people]
    );

    const getClosedCredits = useCallback(
        async (options?: { dateRange?: DateRangeType; customStart?: Date; customEnd?: Date; isPrev?: boolean }) => {
            try {
                setLoadingPeople(true);
                setErrorPeople(null);

                let baseQuery: Query<DocumentData> = collection(db, "people");

                if (options?.dateRange) {
                    const { startDate, endDate } = getDateRange(
                        options.dateRange,
                        options.customStart,
                        options.customEnd,
                        options?.isPrev ?? false
                    );

                    if (startDate) {
                        baseQuery = query(
                            baseQuery,
                            where("creditDetail.closingDay", ">=", startDate),
                            where("creditDetail.closingDay", "<=", endDate)
                        );
                    }
                }

                const snap = await getDocs(baseQuery);

                const fetched: PersonModelInterface[] = snap.docs.map((d) => {
                    const data = d.data() as any;
                    return { id: d.id, ...data } as PersonModelInterface;
                });

                const totalClosed = fetched.filter(
                    (p) => p.status === "closed" || p.status === "closedEarly" || p.status === "returned"
                );

                const payments = fetched.filter((p) => p.hasPayment);

                if (options?.isPrev) {
                    setPrevClosedCredits(totalClosed);
                    setPrevTotalPayments(payments);
                } else {
                    setClosedCredits(totalClosed);
                    setTotalPayments(payments);
                }
            } catch (err) {
                setErrorPeople(err instanceof Error ? err.message : "Ошибка при загрузке закрытых кредитов и выплат");
            } finally {
                setLoadingPeople(false);
            }
        },
        [getDateRange]
    );

    const value = useMemo<PeopleContextProps>(
        () => ({
            people,
            prevPeople,
            closedCredits,
            prevClosedCredits,
            totalPayments,
            prevTotalPayments,
            loadingPeople,
            errorPeople,
            totalCount,
            getPeople,
            getPrevPeople,
            getClosedCredits,
            addPerson,
            getPersonById,
            deletePersonById,
            bulkDelete,
            updatePerson,
            updatePersonStatus,
            toggleChecked,
        }),
        [
            people,
            prevPeople,
            closedCredits,
            prevClosedCredits,
            totalPayments,
            prevTotalPayments,
            loadingPeople,
            errorPeople,
            totalCount,
            getPeople,
            getPrevPeople,
            getClosedCredits,
            addPerson,
            getPersonById,
            deletePersonById,
            bulkDelete,
            updatePerson,
            updatePersonStatus,
            toggleChecked,
        ]
    );

    return <PeopleContext.Provider value={value}>{children}</PeopleContext.Provider>;
};

export const usePeople = () => useContext(PeopleContext);
